import * as React from 'react';

import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import TextField from '@material-ui/core/TextField';

import { grantEmployeeAccess } from './actions/grant-employee-access';
import { loadEmployee } from './actions/load-employee';
import { loadOrg } from '../organization/actions/load-org';
import { userSelector } from '../reducers/current-user-selectors';
import { employeeSelector } from '../reducers/employee-selectors';
import { orgSelector } from '../reducers/org-selectors';

import { UserPermissionForm } from '../admin/user-permission-form';
import { UiButton } from '../ui/ui-button';
import { UiDialog } from '../ui/ui-dialog';

import { employeeName } from './utils/employee-name';
import { HIRING_PERMISSION, EMPLOYEE_PERMISSION } from '../util/permissions';
import { isEmail } from '../util/string-validators';
import { CreateUserSuccessDialog } from '../widgets/create-user-success-dialog';

const mapStateToProps = (state: any, ownProps: InputProps) => {
  const employee = employeeSelector(state, ownProps.employeeId);
  return {
    employee,
    currentUser: userSelector(state),
    org: orgSelector(state, employee.orgId),
  };
};

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  ownProps: InputProps,
) => ({
  actions: {
    loadOrg: (orgId: number) => dispatch(loadOrg(orgId)),
    loadEmployee: () => dispatch(loadEmployee(ownProps.employeeId)),
    grantEmployeeAccess: (orgId: number, update: EmployeeGrantAccessRequest) =>
      dispatch(grantEmployeeAccess(ownProps.employeeId, orgId, update)),
  },
});

type InputProps = {
  employeeId: number;
  onClose: (createdUser?: User) => void;
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & StateProps & DispatchProps;

type State = {
  isEnteringEmail: boolean;
  newEmail: string;
  hiringPermission: string;
  employeesPermission: string;
  isLoading: boolean;
  warningMessage: string | null;
  emailError: string;
  createdUser: User | null;
};

class EmployeeUserDialogComponent extends React.PureComponent<Props, State> {
  state: State = {
    isEnteringEmail: false,
    newEmail: '',
    hiringPermission: HIRING_PERMISSION.ALL,
    employeesPermission: EMPLOYEE_PERMISSION.ALL,
    isLoading: false,
    warningMessage: null,
    emailError: '',
    createdUser: null,
  };

  componentDidMount() {
    this.props.actions.loadEmployee();
    if (this.props.employee) {
      this.props.actions.loadOrg(this.props.employee.orgId);
    }
    this.setIsEnteringEmail();
  }

  componentDidUpdate() {
    this.props.actions.loadEmployee();
    if (this.props.employee) {
      this.props.actions.loadOrg(this.props.employee.orgId);
    }
    this.setIsEnteringEmail();
  }

  setIsEnteringEmail = () => {
    const { employee } = this.props;
    if (employee && !employee.workEmail && !employee.Contact.Person.email) {
      this.setState({ isEnteringEmail: true });
    }
  };

  handleNewEmailClick = () => {
    this.setState({ isEnteringEmail: true });
  };

  handleSendClick = async () => {
    const { newEmail, hiringPermission, employeesPermission } = this.state;
    const { org, actions } = this.props;

    if (!org) {
      return;
    }

    if (newEmail && !isEmail(newEmail)) {
      this.setState({ emailError: 'Please enter a valid email' });
      return;
    }

    this.setState({ isLoading: true }, () => {
      actions
        .grantEmployeeAccess(org.org_id, {
          accountId: org.accountId,
          hiringPermission,
          employeesPermission,
          newEmail,
        })
        .then(({ user, warningMessage }) => {
          this.setState({
            isLoading: false,
            warningMessage: warningMessage || null,
            createdUser: !warningMessage && user ? user : null,
          });
        });
    });
  };

  handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({
      emailError: '',
      newEmail: e.target.value,
    });
  };

  handleHiringPermissionChange = (value: string) => {
    this.setState({ hiringPermission: value });
  };

  handleEmployeesPermissionChange = (value: string) => {
    this.setState({ employeesPermission: value });
  };

  onCreateUserSuccess = () => {
    const { createdUser } = this.state;
    this.props.onClose(createdUser ?? undefined);
  };

  render() {
    const { onClose, employee, currentUser } = this.props;
    const {
      isLoading,
      warningMessage,
      isEnteringEmail,
      newEmail,
      hiringPermission,
      employeesPermission,
      createdUser,
      emailError,
    } = this.state;
    const hasPermissionToNothing =
      employeesPermission === EMPLOYEE_PERMISSION.NONE &&
      hiringPermission === HIRING_PERMISSION.NONE;
    const { firstName } = employee.Contact.Person;

    // only account admins can make new users
    if (!currentUser.isAdmin && !currentUser.isAccountAdmin) {
      return null;
    }

    if (createdUser) {
      return <CreateUserSuccessDialog onClose={this.onCreateUserSuccess} name={firstName} />;
    }
    if (warningMessage) {
      return (
        <UiDialog onClose={onClose} size="xs" title="Add Employee User">
          <UiDialog.Content>
            <div className="u-textAlign-center typo-title">{warningMessage}</div>
          </UiDialog.Content>
        </UiDialog>
      );
    }

    return (
      <UiDialog
        onClose={onClose}
        size="xs"
        title={`Invite ${employeeName(employee)} to Team Engine`}
      >
        <UiDialog.Content>
          {isEnteringEmail ? (
            <>
              <div className="typo-title">What email do you want to use?</div>
              <TextField
                fullWidth
                autoFocus
                label="Email"
                variant="outlined"
                onChange={this.handleEmailChange}
                value={newEmail}
                size="small"
                className="u-margin-top-xl"
              />
              {emailError && <div className="typo-subtitle u-color-red">{emailError}</div>}
            </>
          ) : (
            <div className="typo-title">
              Is{' '}
              <span className="u-fontWeight-500">
                {employee.workEmail || employee.Contact.Person.email}{' '}
              </span>
              the right email for {firstName}? That&apos;s the email they&apos;ll use to log in and
              where we&apos;ll send instructions to get started.
            </div>
          )}
          <UserPermissionForm
            hiringPermission={hiringPermission}
            employeesPermission={employeesPermission}
            onHiringPermissionChange={this.handleHiringPermissionChange}
            onEmployeePermissionChange={this.handleEmployeesPermissionChange}
          />
        </UiDialog.Content>
        {isEnteringEmail ? (
          <UiDialog.Actions>
            <UiButton
              disabled={hasPermissionToNothing}
              onClick={this.handleSendClick}
              loading={isLoading}
            >
              Send
            </UiButton>
          </UiDialog.Actions>
        ) : (
          <UiDialog.Actions>
            <UiButton variant="dismiss" onClick={this.handleNewEmailClick}>
              No, Use A Different Email
            </UiButton>
            <UiButton
              disabled={hasPermissionToNothing}
              onClick={this.handleSendClick}
              className="u-margin-left-sm"
              loading={isLoading}
            >
              Yes, Send
            </UiButton>
          </UiDialog.Actions>
        )}
      </UiDialog>
    );
  }
}

export const EmployeeUserDialog = connect(
  mapStateToProps,
  mapDispatchToProps,
)(EmployeeUserDialogComponent);
